// src/public/tests/IntegrityTestExamples.js
import { useState } from 'react';
import {
    Container, Paper, Typography, Button, Box,
    Divider, LinearProgress, RadioGroup, FormControlLabel, Radio, Stack
} from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const examples = [
    {
        id: 'E1',
        question: 'Disfruto aprender cosas nuevas en mi trabajo.',
        scale: 'A',
        alternatives: [
            'Totalmente en desacuerdo',
            'En desacuerdo',
            'Ni de acuerdo ni en desacuerdo',
            'De acuerdo',
            'Totalmente de acuerdo'
        ]
    },
    {
        id: 'E2',
        question: 'Me gusta organizar mi tiempo para cumplir con mis responsabilidades.',
        scale: 'A',
        alternatives: [
            'Totalmente en desacuerdo',
            'En desacuerdo',
            'Ni de acuerdo ni en desacuerdo',
            'De acuerdo',
            'Totalmente de acuerdo'
        ]
    },
    {
        id: 'E3',
        question: '¿Con qué frecuencia revisas tus correos o mensajes de trabajo antes de comenzar la jornada?',
        scale: 'B',
        alternatives: [
            'Nunca',
            'Rara vez',
            'A veces',
            'Frecuente',
            'Muy frecuente'
        ]
    }
];

const IntegrityTestExamples = ({ onStartTest, loading }) => {
    const { t } = useTranslation();
    const [currentExample, setCurrentExample] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);

    const totalExamples = examples.length;
    const progress = ((currentExample + 1) / totalExamples) * 100;
    const example = examples[currentExample];

    const handleOptionChange = (event) => {
        setSelectedOption(parseInt(event.target.value));
    };

    const handleNext = () => {
        if (currentExample < totalExamples - 1) {
            setCurrentExample(currentExample + 1);
            setSelectedOption(null);
        }
    };

    const canGoNext = selectedOption !== null;

    return (
        <Container maxWidth="md">
            <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
                <Typography variant="h4" gutterBottom align="center">
                    {t('reasoningTests.practiceExamples')}
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                    {t('reasoningTests.practiceExamplesDescription')}
                </Typography>

                {/* Barra de progreso */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('reasoningTests.example')} {currentExample + 1} {t('reasoningTests.of')} {totalExamples}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {Math.round(progress)}%
                        </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 1 }} />
                </Box>

                {/* Pregunta del ejemplo */}
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {example.question}
                    </Typography>
                </Box>

                {/* Opciones (Escala Likert 1-5) */}
                <RadioGroup
                    value={selectedOption !== null ? selectedOption : ''}
                    onChange={handleOptionChange}
                >
                    <Stack spacing={2}>
                        {example.alternatives.map((alternative, index) => (
                            <FormControlLabel
                                key={index}
                                value={index + 1}
                                control={<Radio />}
                                label={
                                    <Typography variant="body1">
                                        {alternative}
                                    </Typography>
                                }
                                sx={{
                                    border: '1px solid',
                                    borderColor: selectedOption === (index + 1) ? 'primary.main' : 'divider',
                                    borderRadius: 1,
                                    p: 2,
                                    m: 0,
                                    bgcolor: selectedOption === (index + 1) ? 'action.selected' : 'transparent',
                                    '&:hover': {
                                        bgcolor: 'action.hover'
                                    }
                                }}
                            />
                        ))}
                    </Stack>
                </RadioGroup>

                {/* Botón siguiente o iniciar */}
                {currentExample < totalExamples - 1 ? (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Button
                            variant="contained"
                            onClick={handleNext}
                            disabled={!canGoNext}
                            size="large"
                            sx={{ minWidth: '200px' }}
                        >
                            {t('reasoningTests.next')}
                        </Button>
                    </Box>
                ) : (
                    <>
                        <Divider sx={{ my: 4 }} />
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                {t('reasoningTests.readyToStart')}
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<PlayArrow />}
                                onClick={onStartTest}
                                disabled={loading || !canGoNext}
                                sx={{ minWidth: '200px' }}
                            >
                                {t('reasoningTests.startTest')}
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>
        </Container>
    );
};

export default IntegrityTestExamples;